import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import {
  Users, UserPlus, Activity, Heart, Sparkles, ShieldCheck, Globe, TrendingUp, Layers, Crown, RefreshCw, Lock, Radio, Zap, Clock, Check, X, ExternalLink, Database, Trash2, Loader2, Mail, DollarSign, CreditCard, Ban,
} from "lucide-react";

const fmt = (n) => (n == null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`);
const NICHE_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#06B6D4", "#F97316", "#84CC16"];
const KIND_LABELS = { welcome: "Welcome", match: "New match", verification_nudge: "24h nudge", partner_verified: "Partner verified", swap_complete: "Swap complete", weekly_digest: "Weekly digest", general: "General" };

function Kpi({ icon: Icon, label, value, sub, tone = "primary" }) {
  const tones = {
    primary: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    green: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    gold: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    rose: "text-rose-400 bg-rose-500/10 border-rose-500/20",
  };
  return (
    <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className={`h-8 w-8 rounded-lg border flex items-center justify-center ${tones[tone]}`}><Icon size={15} /></div>
        {sub && <span className="font-mono text-[10px] text-muted-foreground">{sub}</span>}
      </div>
      <div className="mt-3 font-mono text-2xl font-bold tracking-tight">{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5 uppercase tracking-wide font-mono">{label}</div>
    </div>
  );
}

const chartTip = { contentStyle: { background: "#18181b", border: "1px solid #27272a", borderRadius: 10, fontSize: 12 }, labelStyle: { color: "#a1a1aa" } };

export default function Admin() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [pending, setPending] = useState({ pending: [], count: 0 });
  const [emails, setEmails] = useState({ emails: [], total: 0, failed: 0, sent: 0 });
  const [acting, setActing] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [denied, setDenied] = useState(false);
  const [updated, setUpdated] = useState(null);
  const [blocklist, setBlocklist] = useState({ domains: [], count: 0 });
  const [newDomain, setNewDomain] = useState("");
  const [blocking, setBlocking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const timer = useRef(null);

  const syncStripe = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync-stripe");
      toast.success(`Synced from Stripe — ${data.updated} updated, ${data.downgraded} downgraded (scanned ${data.scanned}).`, { duration: 7000 });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Stripe sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const loadPending = async () => {
    try {
      const { data } = await api.get("/admin/pending-verifications");
      setPending(data);
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/admin/emails");
      setEmails(data);
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/admin/blocklist");
      setBlocklist(data);
    } catch { /* ignore */ }
  };

  const addBlock = async (e) => {
    e?.preventDefault?.();
    const d = newDomain.trim();
    if (!d) return;
    setBlocking(true);
    try {
      await api.post("/admin/blocklist", { domain: d });
      toast.success(`Blocked ${d}`);
      setNewDomain("");
      await loadPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't block that domain.");
    } finally {
      setBlocking(false);
    }
  };

  const removeBlock = async (domain) => {
    try {
      await api.delete(`/admin/blocklist/${encodeURIComponent(domain)}`);
      toast.success(`Unblocked ${domain}`);
      await loadPending();
    } catch {
      toast.error("Couldn't remove that domain.");
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setData(data);
      setUpdated(new Date());
      loadPending();
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 401) setDenied(true);
    }
  };

  const review = async (item, approve) => {
    const key = `${item.match_id}:${item.user_id}`;
    let reason = null;
    if (!approve) {
      reason = window.prompt("Reason shown to the user (optional):", "We couldn't find your backlink on that page.");
      if (reason === null) return; // cancelled
    }
    setActing(key);
    try {
      const url = `/admin/verifications/${item.match_id}/${item.user_id}/${approve ? "approve" : "reject"}`;
      await api.post(url, approve ? {} : { reason });
      toast.success(approve ? "Marked verified — user's dashboard updated." : "Rejected — user asked to resubmit.");
      await load();
    } catch {
      toast.error("Action failed. Try again.");
    } finally {
      setActing(null);
    }
  };

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-demo-sites", { count: 5000 });
      toast.success(data.created > 0 ? `Added ${data.created} demo sites (total ${data.total_demo_sites}).` : (data.message || "Already seeded."));
      await load();
    } catch {
      toast.error("Seeding failed. Try again.");
    } finally {
      setSeeding(false);
    }
  };

  const removeDemo = async () => {
    if (!window.confirm("Remove ALL demo sites? This deletes every seeded account and any matches/swipes tied to them.")) return;
    setRemoving(true);
    try {
      const { data } = await api.post("/admin/remove-demo-sites");
      toast.success(`Removed ${data.removed} demo sites.`);
      await load();
    } catch {
      toast.error("Removal failed. Try again.");
    } finally {
      setRemoving(false);
    }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 10000);
    return () => clearInterval(timer.current);
  }, []);

  if (denied) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <div className="h-14 w-14 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center mx-auto"><Lock className="text-rose-400" size={26} /></div>
        <h1 className="font-serif font-semibold text-2xl mt-5">Admins only</h1>
        <p className="text-muted-foreground text-sm mt-2">This dashboard is restricted. You're signed in without admin access.</p>
        <button onClick={() => nav("/dashboard")} data-testid="admin-back" className="mt-6 rounded-full bg-primary text-white px-6 py-2.5 hover:bg-blue-600 transition-colors">Back to app</button>
      </div>
    );
  }

  if (!data) return <p className="max-w-7xl mx-auto px-6 py-12 font-mono text-sm text-muted-foreground animate-pulse">loading admin metrics…</p>;

  const k = data.kpis;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="admin-panel">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground uppercase">blinxd control room</div>
          <h1 className="font-serif font-semibold text-3xl sm:text-4xl tracking-tight mt-1">Admin dashboard</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-3 py-1.5 text-xs font-mono">
            <Radio size={13} className="animate-pulse" /> LIVE · auto-refresh 10s
          </span>
          <button onClick={load} data-testid="admin-refresh" className="h-9 w-9 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors">
            <RefreshCw size={15} className="text-muted-foreground" />
          </button>
        </div>
      </div>
      {updated && <div className="mt-1 font-mono text-[11px] text-muted-foreground">updated {updated.toLocaleTimeString()}</div>}

      {/* KPI grid */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Kpi icon={Users} label="Total users" value={fmt(k.total_users)} />
        <Kpi icon={UserPlus} label="Signups 24h" value={fmt(k.signups_24h)} sub="today" tone="green" />
        <Kpi icon={UserPlus} label="Signups 7d" value={fmt(k.signups_7d)} sub="week" tone="green" />
        <Kpi icon={TrendingUp} label="Signup trend" value={`${k.signup_trend_pct >= 0 ? "+" : ""}${k.signup_trend_pct}%`} sub="vs prior wk" tone={k.signup_trend_pct >= 0 ? "green" : "rose"} />
        <Kpi icon={Heart} label="Like to match" value={`${k.like_to_match_pct}%`} sub="conversion" tone="rose" />
        <Kpi icon={Activity} label="Active 24h" value={fmt(k.active_24h)} sub="swiped" />
        <Kpi icon={Crown} label="Founding" value={fmt(k.founding_members)} tone="gold" />
        <Kpi icon={Globe} label="Sites" value={fmt(k.sites)} />
        <Kpi icon={Layers} label="Total swipes" value={fmt(k.total_swipes)} />
        <Kpi icon={Heart} label="Likes" value={fmt(k.likes)} tone="rose" />
        <Kpi icon={Zap} label="Matches" value={fmt(k.matches_total)} />
        <Kpi icon={Sparkles} label="AI articles" value={fmt(k.ai_articles)} tone="primary" />
        <Kpi icon={ShieldCheck} label="Verified links" value={fmt(k.verified_links)} tone="green" />
        <Kpi icon={Clock} label="Awaiting verify" value={fmt(k.pending_verifications)} tone="gold" sub="manual" />
        <Kpi icon={TrendingUp} label="Active swaps" value={fmt(k.active_swaps)} sub={`${k.completed_swaps} done`} />
      </div>

      {/* Subscriptions / sales */}
      <div className="mt-4 rounded-xl border border-white/10 bg-zinc-900/60 p-5" data-testid="subscriptions-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground uppercase flex items-center gap-2"><CreditCard size={13} /> Subscriptions · sales</div>
          <div className="flex items-center gap-2">
            <span className="rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2.5 py-1 font-mono text-[11px]" data-testid="sub-mrr">${fmt(data.subscriptions?.est_mrr || 0)}/mo est. MRR</span>
            <span className="rounded-md bg-white/5 text-muted-foreground border border-white/10 px-2.5 py-1 font-mono text-[11px]" data-testid="sub-arr">${fmt(data.subscriptions?.est_arr || 0)}/yr ARR</span>
            <button onClick={syncStripe} disabled={syncing} data-testid="sync-stripe-button"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary/15 border border-primary/30 text-primary px-2.5 py-1 font-mono text-[11px] hover:bg-primary/25 transition-colors disabled:opacity-50">
              {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} {syncing ? "Syncing…" : "Sync from Stripe"}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Kpi icon={DollarSign} label="Paying" value={fmt(data.subscriptions?.paying_total || 0)} tone="green" />
          <Kpi icon={Zap} label="Pro subs" value={fmt(data.subscriptions?.paid_pro || 0)} tone="primary" />
          <Kpi icon={Crown} label="Premium subs" value={fmt(data.subscriptions?.paid_premium || 0)} tone="gold" />
          <Kpi icon={UserPlus} label="New subs 7d" value={fmt(data.subscriptions?.new_subs_7d || 0)} sub="week" tone="green" />
          <Kpi icon={UserPlus} label="New subs 30d" value={fmt(data.subscriptions?.new_subs_30d || 0)} sub="month" />
          <Kpi icon={X} label="Canceled" value={fmt(data.subscriptions?.canceled || 0)} tone="rose" />
        </div>
        {(data.subscriptions?.recent || []).length > 0 ? (
          <div className="mt-4 max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-1 pb-2 font-normal">Customer</th>
                  <th className="px-1 pb-2 font-normal">Plan</th>
                  <th className="px-1 pb-2 font-normal">Status</th>
                  <th className="px-1 pb-2 font-normal text-right">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.subscriptions.recent.map((s, i) => (
                  <tr key={i} className="border-t border-white/5" data-testid="subscription-row">
                    <td className="px-1 py-2 truncate max-w-[240px]">{s.email}</td>
                    <td className="px-1 py-2"><span className={`font-mono text-[11px] rounded px-1.5 py-0.5 border ${s.plan === "premium" ? "bg-amber-500/10 text-amber-400 border-amber-500/20" : "bg-primary/10 text-primary border-primary/20"}`}>{s.plan}</span></td>
                    <td className="px-1 py-2"><span className={`font-mono text-[11px] ${s.status === "canceled" ? "text-rose-400" : "text-emerald-400"}`}>{s.status}</span></td>
                    <td className="px-1 py-2 text-right font-mono text-[11px] text-muted-foreground whitespace-nowrap">{s.at ? new Date(s.at).toLocaleDateString() : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-4 text-sm text-muted-foreground">No paid subscriptions yet. Live sales will appear here as members upgrade to Pro or Premium.</div>
        )}
      </div>

      {/* Demo data controls */}
      <div className="mt-4 rounded-xl border border-white/10 bg-zinc-900/60 p-5 flex items-center justify-between flex-wrap gap-4" data-testid="demo-data-controls">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg border border-blue-500/20 bg-blue-500/10 text-blue-400 flex items-center justify-center"><Database size={16} /></div>
          <div>
            <div className="text-sm font-medium">Demo sites in the pool</div>
            <div className="text-xs text-muted-foreground">Populate matching with realistic sites across niches & DA (skewed to lower DA). <span className="font-mono text-foreground" data-testid="demo-sites-count">{fmt(k.demo_sites || 0)}</span> active.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={seedDemo} disabled={seeding || removing} data-testid="seed-demo-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 border border-primary/30 text-blue-300 px-4 py-2 text-sm hover:bg-primary/25 transition-colors disabled:opacity-50">
            {seeding ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {seeding ? "Seeding…" : "Seed 5,000 sites"}
          </button>
          <button onClick={removeDemo} disabled={removing || seeding || !(k.demo_sites > 0)} data-testid="remove-demo-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-400 px-4 py-2 text-sm hover:bg-rose-500/25 transition-colors disabled:opacity-50">
            {removing ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} {removing ? "Removing…" : "Remove all demo"}
          </button>
        </div>
      </div>

      {/* Domain blocklist manager */}
      <div className="mt-4 rounded-xl border border-white/10 bg-zinc-900/60 p-5" data-testid="blocklist-manager">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg border border-rose-500/20 bg-rose-500/10 text-rose-400 flex items-center justify-center"><Ban size={15} /></div>
            <div className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground uppercase">Domain blocklist · signup guard</div>
          </div>
          <span className="rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-400 px-3 py-1 text-xs font-mono" data-testid="blocklist-count">
            {blocklist.count} blocked
          </span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          These domains (and their subdomains) can't be used to sign up or add a site. Add well-known brands to keep the pool clean.
        </p>
        <form onSubmit={addBlock} className="flex items-center gap-2 mb-4">
          <input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} data-testid="blocklist-input"
            placeholder="example.com"
            className="flex-1 rounded-lg bg-zinc-950 border border-white/10 px-3 h-10 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-rose-500/40" />
          <button type="submit" disabled={blocking || !newDomain.trim()} data-testid="blocklist-add-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-400 px-4 h-10 text-sm hover:bg-rose-500/25 transition-colors disabled:opacity-50">
            {blocking ? <Loader2 size={15} className="animate-spin" /> : <Ban size={15} />} Block
          </button>
        </form>
        {blocklist.domains.length === 0 ? (
          <p className="text-sm text-muted-foreground py-1" data-testid="blocklist-empty">No blocked domains yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="blocklist-chips">
            {blocklist.domains.map((d) => (
              <span key={d.domain} data-testid={`blocklist-chip-${d.domain}`}
                className="group inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 pl-3 pr-1.5 py-1 text-xs font-mono text-muted-foreground">
                {d.domain}
                <button onClick={() => removeBlock(d.domain)} data-testid={`blocklist-remove-${d.domain}`}
                  className="h-5 w-5 rounded-full flex items-center justify-center text-muted-foreground hover:bg-rose-500/20 hover:text-rose-400 transition-colors">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Manual verification queue */}
      <div className="mt-4 rounded-xl border border-white/10 bg-zinc-900/60 p-5" data-testid="pending-verifications">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg border border-amber-500/20 bg-amber-500/10 text-amber-400 flex items-center justify-center"><Clock size={15} /></div>
            <div className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground uppercase">Awaiting verification · manual queue</div>
          </div>
          <span className="rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 px-3 py-1 text-xs font-mono" data-testid="pending-count">
            {pending.count} pending
          </span>
        </div>
        {pending.pending.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2" data-testid="pending-empty">Nothing to review — all submitted links are verified or auto-confirmed. 🎉</p>
        ) : (
          <div className="space-y-2">
            {pending.pending.map((item) => {
              const key = `${item.match_id}:${item.user_id}`;
              const busy = acting === key;
              return (
                <div key={key} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 flex items-center gap-4 flex-wrap" data-testid={`pending-row-${item.user_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate flex items-center gap-2">
                      {item.user_name || item.user_email}
                      <span className="font-mono text-[10px] text-muted-foreground truncate">{item.user_email}</span>
                    </div>
                    <div className="mt-1.5 grid sm:grid-cols-2 gap-x-6 gap-y-1">
                      <div className="min-w-0">
                        <div className="font-mono text-[9px] tracking-widest text-muted-foreground uppercase">Published at</div>
                        <a href={item.published_url} target="_blank" rel="noreferrer" data-testid="pending-published-url"
                          className="font-mono text-[12px] text-blue-400 hover:underline truncate flex items-center gap-1">
                          <ExternalLink size={11} className="shrink-0" /><span className="truncate">{item.published_url}</span>
                        </a>
                      </div>
                      <div className="min-w-0">
                        <div className="font-mono text-[9px] tracking-widest text-muted-foreground uppercase">Backlink should point to</div>
                        <div className="font-mono text-[12px] text-emerald-400 truncate" data-testid="pending-target">{item.backlink_target || "—"}</div>
                      </div>
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground mt-1">
                      partner: {item.partner_name || "—"} · since {item.pending_since ? new Date(item.pending_since).toLocaleString() : "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => review(item, true)} disabled={busy} data-testid={`approve-${item.user_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 px-4 py-2 text-sm hover:bg-emerald-500/25 transition-colors disabled:opacity-50">
                      <Check size={15} /> {busy ? "…" : "Verify"}
                    </button>
                    <button onClick={() => review(item, false)} disabled={busy} data-testid={`reject-${item.user_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-400 px-4 py-2 text-sm hover:bg-rose-500/25 transition-colors disabled:opacity-50">
                      <X size={15} /> Reject
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Charts */}
      <div className="mt-4 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-white/10 bg-zinc-900/60 p-5">
          <div className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground uppercase mb-4">Signups &amp; swipes · last 14 days</div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.series} margin={{ left: -18, right: 6, top: 4 }}>
              <defs>
                <linearGradient id="gSu" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} /><stop offset="100%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient>
                <linearGradient id="gSw" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.4} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="date" stroke="#52525b" fontSize={11} tickLine={false} />
              <YAxis stroke="#52525b" fontSize={11} tickLine={false} allowDecimals={false} />
              <Tooltip {...chartTip} />
              <Area type="monotone" dataKey="swipes" stroke="#10B981" fill="url(#gSw)" strokeWidth={2} />
              <Area type="monotone" dataKey="signups" stroke="#3B82F6" fill="url(#gSu)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-5">
          <div className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground uppercase mb-4">DA distribution</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.da_distribution} margin={{ left: -22, right: 6, top: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="bucket" stroke="#52525b" fontSize={11} tickLine={false} />
              <YAxis stroke="#52525b" fontSize={11} tickLine={false} allowDecimals={false} />
              <Tooltip {...chartTip} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {data.da_distribution.map((_, i) => <Cell key={i} fill="#3B82F6" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-4 grid lg:grid-cols-3 gap-4">
        {/* Niches */}
        <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-5">
          <div className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground uppercase mb-4">Sites by niche</div>
          {data.niches.length === 0 ? <p className="text-sm text-muted-foreground">No sites yet.</p> : (
            <div className="space-y-2.5">
              {data.niches.map((n, i) => {
                const max = data.niches[0].count || 1;
                return (
                  <div key={n.niche} className="flex items-center gap-3">
                    <span className="w-24 text-xs text-muted-foreground truncate">{n.niche}</span>
                    <div className="flex-1 h-3 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(n.count / max) * 100}%`, background: NICHE_COLORS[i % NICHE_COLORS.length] }} />
                    </div>
                    <span className="font-mono text-xs w-6 text-right">{n.count}</span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="rounded-md bg-white/5 px-2 py-1 font-mono">Google: {k.google_signups}</span>
            <span className="rounded-md bg-white/5 px-2 py-1 font-mono">Pipeline: {k.pipeline}</span>
            <span className="rounded-md bg-white/5 px-2 py-1 font-mono">Passes: {k.passes}</span>
          </div>
        </div>

        {/* Top sites */}
        <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-5">
          <div className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground uppercase mb-4">Top sites by traffic</div>
          <div className="space-y-2">
            {data.top_sites.map((s) => (
              <div key={s.url} className="flex items-center justify-between gap-3 text-sm">
                <span className="font-mono text-xs truncate text-foreground">{s.url}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="rounded bg-blue-500/10 text-blue-400 px-1.5 py-0.5 font-mono text-[10px]">DA {s.da}</span>
                  <span className="font-mono text-xs text-emerald-400">{fmt(s.traffic)}/mo</span>
                </div>
              </div>
            ))}
            {data.top_sites.length === 0 && <p className="text-sm text-muted-foreground">No sites yet.</p>}
          </div>
        </div>

        {/* Recent signups */}
        <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-5">
          <div className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground uppercase mb-4">Recent signups</div>
          <div className="space-y-2.5">
            {data.recent_signups.map((u, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm truncate flex items-center gap-1.5">{u.name || u.email}{u.founding && <Crown size={11} className="text-amber-400 shrink-0" />}</div>
                  <div className="font-mono text-[10px] text-muted-foreground truncate">{u.email}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[11px] text-muted-foreground">{u.niche || "no site"}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Site traffic — first-party page views + unique visitors */}
      <div className="mt-4 rounded-xl border border-white/10 bg-zinc-900/60 p-5" data-testid="site-traffic-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground uppercase">Site traffic · last 14 days</div>
          <div className="flex items-center gap-2">
            <span className="rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2.5 py-1 font-mono text-[11px]" data-testid="traffic-views-24h">{fmt(k.views_24h)} views · 24h</span>
            <span className="rounded-md bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2.5 py-1 font-mono text-[11px]" data-testid="traffic-uniques-24h">{fmt(k.uniques_24h)} visitors · 24h</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={data.traffic_series} margin={{ left: -18, right: 6, top: 4 }}>
            <defs>
              <linearGradient id="gPv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} /><stop offset="100%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient>
              <linearGradient id="gUv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F59E0B" stopOpacity={0.45} /><stop offset="100%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="date" stroke="#52525b" fontSize={11} tickLine={false} />
            <YAxis stroke="#52525b" fontSize={11} tickLine={false} allowDecimals={false} />
            <Tooltip {...chartTip} />
            <Area type="monotone" dataKey="views" name="Page views" stroke="#3B82F6" fill="url(#gPv)" strokeWidth={2} />
            <Area type="monotone" dataKey="uniques" name="Unique visitors" stroke="#F59E0B" fill="url(#gUv)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
        <div className="mt-3 flex items-center gap-4 text-[11px] text-muted-foreground font-mono flex-wrap">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-500" /> Page views</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> Unique visitors</span>
          <span className="sm:ml-auto">7d: {fmt(k.views_7d)} views · {fmt(k.uniques_7d)} visitors · {fmt(k.total_views)} all-time</span>
        </div>
      </div>

      {/* Email log — confirm delivery at a glance */}
      <div className="mt-4 rounded-xl border border-white/10 bg-zinc-900/60 p-5" data-testid="email-log-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground uppercase flex items-center gap-2"><Mail size={13} /> Email log · recent</div>
          <div className="flex items-center gap-2">
            <span className="rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2.5 py-1 font-mono text-[11px]" data-testid="email-sent-count">{fmt(emails.sent)} sent</span>
            <span className="rounded-md bg-rose-500/10 text-rose-400 border border-rose-500/20 px-2.5 py-1 font-mono text-[11px]" data-testid="email-failed-count">{fmt(emails.failed)} failed</span>
          </div>
        </div>
        {emails.emails.length === 0 ? (
          <div className="text-sm text-muted-foreground">No emails sent yet. They'll appear here as welcome, match, nudge and digest emails go out.</div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-1 pb-2 font-normal">Type</th>
                  <th className="px-1 pb-2 font-normal">Recipient</th>
                  <th className="px-1 pb-2 font-normal">Status</th>
                  <th className="px-1 pb-2 font-normal text-right">Time</th>
                </tr>
              </thead>
              <tbody>
                {emails.emails.map((e, i) => (
                  <tr key={i} className="border-t border-white/5" data-testid="email-log-row">
                    <td className="px-1 py-2"><span className="font-mono text-[11px] rounded bg-white/5 border border-white/10 px-1.5 py-0.5 whitespace-nowrap">{KIND_LABELS[e.kind] || e.kind}</span></td>
                    <td className="px-1 py-2 truncate max-w-[240px]">{e.to}</td>
                    <td className="px-1 py-2">
                      <span className={`inline-flex items-center gap-1 font-mono text-[11px] ${e.status === "sent" ? "text-emerald-400" : "text-rose-400"}`}>
                        {e.status === "sent" ? <Check size={12} /> : <X size={12} />}{e.status}
                      </span>
                    </td>
                    <td className="px-1 py-2 text-right font-mono text-[11px] text-muted-foreground whitespace-nowrap">{e.at ? new Date(e.at).toLocaleString() : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
